import type { FC } from 'react'

import type { WhiteLabel } from '@skytech/types'

import { useAppContext } from '@/app/index.context'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'

type AmountProps = {
  amount: number
  currency: string
  onClick: (
    currency: string,
    e?: React.KeyboardEvent<HTMLSpanElement> | React.MouseEvent<HTMLSpanElement>
  ) => void
  isActive?: boolean
  hasHover?: boolean
  rotateColors?: boolean
  tabIndex?: number
}

type Styles = {
  primaryColor: string
  secondaryColor: string
  hoverPrimary: string
  hoverSecondary: string
}

export const Amount: FC<AmountProps> = ({
  amount = 0,
  currency,
  isActive = false,
  rotateColors = false,
  hasHover = true,
  onClick,
  tabIndex,
}) => {
  const { whiteLabel } = useAppContext()

  const baseClasses = 'cursor-pointer transition-opacity duration-200 ease-out'

  const variants: Record<WhiteLabel, Styles> = {
    control: {
      primaryColor: 'text-skytech-orange',
      secondaryColor: 'text-gray-900',
      hoverPrimary: 'hover:text-skytech-orange',
      hoverSecondary: 'hover:text-gray-900',
    },
    skytech: {
      primaryColor: 'text-skytech-orange',
      secondaryColor: 'text-gray-900',
      hoverPrimary: 'hover:text-skytech-orange',
      hoverSecondary: 'hover:text-gray-900',
    },
    tcm: {
      primaryColor: 'text-skytech-orange',
      secondaryColor: 'text-gray-900',
      hoverPrimary: 'hover:text-skytech-orange',
      hoverSecondary: 'hover:text-gray-900',
    },
    arp: {
      primaryColor: 'text-arp',
      secondaryColor: 'text-gray-900',
      hoverPrimary: 'hover:text-arp',
      hoverSecondary: 'hover:text-gray-900',
    },
    mobit: {
      primaryColor: 'text-mobit',
      secondaryColor: 'text-gray-900',
      hoverPrimary: 'hover:text-mobit',
      hoverSecondary: 'hover:text-gray-900',
    },
    evry: {
      primaryColor: 'text-tietoevry',
      secondaryColor: 'text-gray-900',
      hoverPrimary: 'hover:text-tietoevry',
      hoverSecondary: 'hover:text-gray-900',
    },
    tietoevry: {
      primaryColor: 'text-tietoevry',
      secondaryColor: 'text-gray-900',
      hoverPrimary: 'hover:text-tietoevry',
      hoverSecondary: 'hover:text-gray-900',
    },
    bechtle: {
      primaryColor: 'text-bechtle',
      secondaryColor: 'text-gray-900',
      hoverPrimary: 'hover:text-bechtle',
      hoverSecondary: 'hover:text-gray-900',
    },
    csi: {
      primaryColor: 'text-csi',
      secondaryColor: 'text-gray-900',
      hoverPrimary: 'hover:text-csi',
      hoverSecondary: 'hover:text-gray-900',
    },
    telia: {
      primaryColor: 'text-telia',
      secondaryColor: 'text-gray-900',
      hoverPrimary: 'hover:text-telia',
      hoverSecondary: 'hover:text-gray-900',
    },
    komplett: {
      primaryColor: 'text-komplett',
      secondaryColor: 'text-gray-900',
      hoverPrimary: 'hover:text-komplett',
      hoverSecondary: 'hover:text-gray-900',
    },
    elkjop: {
      primaryColor: 'text-elkjop',
      secondaryColor: 'text-gray-900',
      hoverPrimary: 'hover:text-elkjop',
      hoverSecondary: 'hover:text-gray-900',
    },
    ice: {
      primaryColor: 'text-skytech',
      secondaryColor: 'text-gray-900',
      hoverPrimary: 'hover:text-skytech',
      hoverSecondary: 'hover:text-gray-900',
    },
    threeStepIT: {
      primaryColor: 'text-threeStepIT',
      secondaryColor: 'text-gray-900',
      hoverPrimary: 'hover:text-threeStepIT',
      hoverSecondary: 'hover:text-gray-900',
    },
  }

  const style = variants[whiteLabel] || variants.control
  let hover = rotateColors ? style.hoverPrimary : style.hoverSecondary
  const primary = rotateColors ? style.secondaryColor : style.primaryColor
  const secondary = rotateColors ? style.primaryColor : style.secondaryColor
  const textColor = isActive ? secondary : primary
  hover = hasHover ? hover : ''

  return (
    <span
      key={currency}
      onClick={e => onClick(currency, e)}
      role="button"
      onKeyDown={e => onClick(currency, e)}
      tabIndex={tabIndex}
      className={`${baseClasses} ${hover} ${textColor}`}
    >
      <div className={`amount ${amount === 0 ? 'text-gray-300' : ''}`}>
        <FormattedNumberWithCurrency amount={amount || 0} currency={currency} />
      </div>
    </span>
  )
}
